/**
 * Validates a credit card number using the Luhn algorithm
 * @param cardNumber - The credit card number to validate
 * @returns Boolean indicating if the card number is valid
 */
import { capitalize, get } from "lodash";
import * as Yup from "yup";
import type { PaymentPack } from "../types";

export const validateCardNumber = (cardNumber: string): boolean => {
  if (!cardNumber) return false;

  // Remove all non-digit characters
  const sanitizedNumber = cardNumber.replace(/\D/g, "");

  if (sanitizedNumber.length < 13 || sanitizedNumber.length > 19) {
    return false;
  }

  // Luhn algorithm implementation
  let sum = 0;
  let shouldDouble = false;

  // Loop through the digits in reverse order
  for (let i = sanitizedNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(sanitizedNumber.charAt(i));

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

/**
 * Validates credit card security code (CVV/CVC)
 * @param securityCode - The security code to validate
 * @returns Boolean indicating if the security code is valid
 */
export const validateSecurityCode = (securityCode: string): boolean => {
  if (!securityCode) return false;

  const sanitizedCode = securityCode.replace(/\D/g, "");

  // CVV is typically 3 digits, but Amex uses 4 digits
  return /^\d{3,4}$/.test(sanitizedCode);
};

/**
 * Detects credit card type based on the card number
 * @param cardNumber - The credit card number
 * @returns The detected card type or null if not recognized
 */
export const detectCardType = (cardNumber: string): string | null => {
  if (!cardNumber) return null;

  const sanitizedNumber = cardNumber.replace(/\D/g, "");

  // Card type regex patterns
  const patterns = {
    visa: /^4\d{12}(?:\d{3})?$/,
    mastercard: /^5[1-5]\d{14}$/,
    amex: /^3[47]\d{13}$/,
    discover: /^6(?:011|5\d{2})\d{12}$/,
  };

  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(sanitizedNumber)) {
      return type;
    }
  }

  return null;
};

export const getCardProviderName = (cardNumber: string): string => {
  const cardType = detectCardType(cardNumber);
  if (cardType == "amex") {
    return "American Express";
  }

  return cardType ? capitalize(cardType) : "Card";
};

/**
 * Formats a credit card number with spaces for better readability
 * @param cardNumber - The credit card number to format
 * @returns Formatted credit card number
 */
export const formatCardNumber = (cardNumber: string): string => {
  if (!cardNumber) return "";

  const sanitizedNumber = cardNumber.replace(/\D/g, "");
  const cardType = detectCardType(sanitizedNumber);

  // Format based on card type
  if (cardType === "amex") {
    // AMEX: XXXX XXXXXX XXXXX
    return sanitizedNumber.replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
  } else {
    // Other cards: XXXX XXXX XXXX XXXX
    return sanitizedNumber.replace(/(\d{4})(?=\d)/g, "$1 ");
  }
};

/**
 * Validates a zip code
 * @param zipCode - The zip code to validate
 * @returns Boolean indicating if the zip code is valid
 */
export const validateZipCode = (zipCode: string): boolean => {
  if (!zipCode) return false;

  // US zip code format (5 digits or 5+4 digits)
  return /^\d{5}(-\d{4})?$/.test(zipCode);
};

export const getPaymentFormValidationSchema = (packs: PaymentPack[]) => {
  let licensesCountSchema = Yup.number()
    .required("Number of Users is required")
    .positive("Must be a positive number")
    .max(5000000, "Number of Licenses cannot exceed 5,000,000")
    .test("min-needed-licenses", function (value) {
      const path = this.path.replace(/licensesCount$/, "purchasingLicenses");
      const originalValue = get({ packs }, path);

      if (value >= originalValue) return true;

      return this.createError({
        message: `Enter a number equal to or greater than the licenses in use: <strong>${originalValue}</strong>`,
      });
    });

  return Yup.object().shape({
    packs: Yup.array().of(Yup.object().shape({ licensesCount: licensesCountSchema })),
    creditCardInfo: Yup.object().shape({
      nameOnCard: Yup.string().required("Name on card is required").min(2, "Name is too short"),

      cardNumber: Yup.string()
        .required("Card number is required")
        .test("valid-card", "Please enter a valid credit card number", (value) => validateCardNumber(value)),

      expirationMonth: Yup.string()
        .required("Month is required")
        .matches(/^(0[1-9]|1[0-2])$/, "Month must be 2 digits (01-12)"),

      expirationYear: Yup.string()
        .required("Year is required")
        .matches(/^\d{2}$/, "Year must be 2 digits")
        .test("future-date", "Expiration date must be in the future", function (year) {
          const month = this.parent.expirationMonth;
          if (!month || !year) return false;

          const currentDate = new Date();
          const currentYear = currentDate.getFullYear() % 100;
          const currentMonth = currentDate.getMonth() + 1;
          const expYear = parseInt(year, 10);
          const expMonth = parseInt(month, 10);

          return expYear > currentYear || (expYear === currentYear && expMonth >= currentMonth);
        }),

      securityCode: Yup.string()
        .required("Security code is required")
        .test("valid-cvv", "Please enter a valid security code", (value) => validateSecurityCode(value)),
    }),

    billingInfo: Yup.object().shape({
      streetAddress: Yup.string().required("Street address is required").min(5, "Address is too short"),

      city: Yup.string().required("City is required").min(2, "City name is too short"),

      state: Yup.string().required("State is required"),

      zipCode: Yup.string()
        .required("Zip code is required")
        .test("valid-zip", "Please enter a valid zip code", (value) => validateZipCode(value)),

      country: Yup.string().required("Country is required"),
    }),
  });
};
